import { useEffect, useMemo, useRef, useState } from "react";
import type {
  KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent, ReactElement,
  ReactNode,
} from "react";
import { ApiError } from "@kundeninfo/api-client";
import type {
  ApiClient, EchteBelege, Layout, LayoutGrafik, LayoutVorschauAnfrage,
} from "@kundeninfo/api-client";
import { Arbeitsdialog, Modal, ModalFuss, darfAbbrechen } from "@kundeninfo/ui";
import { Hinweis, Knopf, Steckbrief } from "@kundeninfo/ui/me-design";
import { LayoutBlatt } from "./LayoutBlatt.js";
import { LayoutRegisterPanel, LayoutReiterleiste, panelId } from "./LayoutRegister.js";
import type { RegisterEintrag, RegisterId, ReiterleisteElement } from "./LayoutRegister.js";
import { feldId } from "./register/felder.js";
import { SeitePanel } from "./register/Seite.js";
import { SektionenPanel } from "./register/Sektionen.js";
import { TabellePanel } from "./register/Tabelle.js";
import { BloeckePanel } from "./register/Bloecke.js";
import { TextePanel } from "./register/Texte.js";
import { MarkePanel, STEMPEL_VORGABE, UNTERSCHRIFT_VORGABE } from "./register/Marke.js";
import type { GrafikTeil, GrafikWege } from "./register/Marke.js";
import { useLayoutVorschau } from "./useLayoutVorschau.js";
import {
  aufZehntel, fehlerJeReiter, feldFehlerKarte, grafikAufSeite, grafikNameAus, istGleich, mmJePixel,
  ohneGeaenderte, reiterFuer, spaltenAus, spaltenLinien, verschoben, wertAn, zahlAus,
} from "./layoutEntwurf.js";
import type { Entwurf } from "./layoutEntwurf.js";
import "./Layout.css";

/**
 * DER LAYOUT-EDITOR (Spec „Layout-Editor, Etappe 1", Oberfläche) — seit dem Paket
 * „Editor-Arbeitsdialog“ (Aufgabe 2; Entscheide 27, 53 a, 55) ein vollflächiger
 * ARBEITSDIALOG über der Schale (`Arbeitsdialog` aus @kundeninfo/ui, Titel = Bereichsname
 * „Belegdesign“). Kopf, Meldungen, Reiter und Fussleiste sind Slots des Elements:
 *
 *  - `steckbrief`: `Steckbrief art="dialog"` mit Layoutname/Unternehmen als Titel, der
 *    Ablage-Kennung, dem Status-Chip und den Kennwerten (Falt-Knopf vom Element);
 *  - `meldungen`: Fremder Stand, Störung, Quittung, Ablage-Hinweis — oben, vor allem
 *    anderen (Hausregel), Schlechtestes oben;
 *  - die Reiterleiste (Fehlerzähler je Reiter) steht im Körper direkt über ihrem Panel —
 *    der Slot `register` bleibt leer (Review A2 W1, me-design Befund 30);
 *  - Fuss: links „Verwerfen“, Mitte der Zustandstext, rechts „Speichern“ und „Freigeben“.
 *
 * Der KÖRPER (Werkzeugleiste, Blatt, Gestaltungspanel als Grid) bleibt derselbe Baum über
 * Reiter- und Belegwechsel — kein Remount, der Scrollstand bleibt. Escape und Kreuz gehen
 * über `onAbbrechen`: bei ungespeicherten Eingaben die Verwerfen-Rückfrage, dann
 * `onSchliessen` (zur Layoutliste). Subdialoge sind `Modal` im Baum des Arbeitsdialogs und
 * liegen damit gestapelt darüber.
 *
 * Der ENTWURF lebt hier lokal (`entwurf`) neben dem Serverstand (`stand`); „ungespeichert"
 * ist der Unterschied der beiden. Grafik-Wege (K10) antworten mit einem neuen Stand —
 * übernommen wird dann nur Version und Grafiken, der lokale Entwurf bleibt.
 *
 * Farben ausschliesslich über --ki-* (Körper) bzw. vom Element, Block `lay__`, keine
 * Domänen-Importe.
 */

export type LayoutEditorClient = Pick<ApiClient, "getLayout" | "saveLayoutDraft" |
  "previewLayout" | "listLayoutRealDocuments" | "publishLayout" | "discardLayout" |
  "adoptLayoutStore" | "createLayoutGraphic" | "updateLayoutGraphic" | "deleteLayoutGraphic" |
  "reorderLayoutGraphics" | "layoutGraphicUrl" | "layoutGraphicImageUrl">;

export type LayoutEditorProps = {
  client: LayoutEditorClient;
  layoutId: string;
  onUnternehmen: (companyId: string) => void;
  onZustand: (z: { ungespeichert: boolean; listeNeu: boolean }) => void;
  /** Der Arbeitsdialog geht zu (nach der Verwerfen-Rückfrage, Entscheid 55). */
  onSchliessen: () => void;
};

// Die Belegarten und ihre Wortlaute kommen aus `katalog.belegarten` der Antwort — die
// Oberfläche kennt die Domäne nicht (Hausregel).
type Belegart = LayoutVorschauAnfrage["belegart"];

type BelegWahl =
  | { art: "MUSTER" }
  | { art: "ECHT"; quelle: "OFFER" | "SALES_ORDER" | "INVOICE"; id: string; titel: string };

type Meldung = { text: string; art: "ok" | "schlecht" };

const DATUM = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

function meldungAus(f: unknown, ersatz: string): string {
  return f instanceof ApiError ? f.message : ersatz;
}

function mm(wert: unknown, system: string): number {
  const eigen = Number(zahlAus(wert, "mm"));
  return Number.isFinite(eigen) && zahlAus(wert, "mm") !== "" ? eigen : Number(zahlAus(system, "mm"));
}

export function LayoutEditor(p: LayoutEditorProps): ReactElement {
  const [stand, setStand] = useState<Layout | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [entwurf, setEntwurf] = useState<Entwurf>({});
  // Der JÜNGSTE Entwurf auch zwischen zwei Zeichnungen: Ein Register ruft `onEntwurf(neu)`
  // und gleich danach `onFeldVerlassen()` (Schalter, Auswahl, Pfeile) — ein Auftrag aus
  // dem Abschluss dieser Zeichnung trüge noch den alten Entwurf.
  const entwurfRef = useRef<Entwurf>({});
  function entwurfSetzen(neu: Entwurf): void {
    entwurfRef.current = neu;
    setEntwurf(neu);
  }
  // Feldfehler aus zwei Quellen (Spec: die Whitelist greift beim Speichern UND vor der
  // Vorschau): `speicherFehler` aus einer 422 des Speicherns, `vorschauFehler` aus einer
  // 422 der Vorschau. Beide räumt die Änderung des betroffenen Felds (Fixrunde 1, W2).
  const [speicherFehler, setSpeicherFehler] = useState<Record<string, string>>({});
  const [vorschauFehler, setVorschauFehler] = useState<Record<string, string>>({});
  /** Eine Eingabe im Register: neuer Entwurf, die Befunde geänderter Felder fallen weg. */
  function eingabe(neu: Entwurf): void {
    const alt = entwurfRef.current;
    entwurfSetzen(neu);
    setSpeicherFehler((f) => ohneGeaenderte(f, alt, neu));
    setVorschauFehler((f) => ohneGeaenderte(f, alt, neu));
  }
  // Nach einer 409 (R8): Der Serverstand ist fremd — Speichern, Verwerfen und Freigeben
  // bleiben gesperrt, bis „Neu laden" die Sicht geholt hat (Fixrunde 1, W1).
  const [fremd, setFremd] = useState<{ von: string; am: string } | null>(null);
  const [meldung, setMeldung] = useState<Meldung | null>(null);
  const [belegart, setBelegart] = useState<Belegart>("ANGEBOT");
  const [beleg, setBeleg] = useState<BelegWahl>({ art: "MUSTER" });
  const [dialog, setDialog] = useState<null | "echt" | "freigabe" | "verwerfen" | "neuladen">(null);
  const [echte, setEchte] = useState<EchteBelege["items"] | null>(null);
  const [arbeitet, setArbeitet] = useState(false);
  const [zoom, setZoom] = useState<"breite" | "voll">("breite");
  const [hilfslinien, setHilfslinien] = useState(false);
  const [spaltenlinien, setSpaltenlinien] = useState(false);
  const [register, setRegister] = useState<RegisterId>("seite");
  // Ein ZUG im Blatt (K10): die Lage unterwegs (Rahmen und Register zeigen sie), ohne
  // Serveraufruf; gespeichert wird beim Loslassen. `zugAufraeumen` nimmt die Horcher am
  // Fenster wieder ab — auch, wenn der Editor mitten im Zug abgebaut wird.
  const [grafikFehler, setGrafikFehler] =
    useState<{ gid: string; felder: Record<string, string>; meldung?: string } | null>(null);
  // LAUFSPERRE der Grafik-Wege (Fixrunde 2, H1): Ein Weg, der während eines anderen
  // ausgelöst wird (Blur beim Loslassen eines Zugs, zwei Verlassen in einem Takt), wartet
  // in der Kette und startet mit dem JÜNGSTEN Stand — Version und Felder aus `standRef`,
  // die `uebernimm` synchron führt. So endet keiner mit einer alten Version in einer 409.
  const standRef = useRef<Layout | null>(null);
  const konfliktRef = useRef(false);
  const wegKette = useRef<Promise<void>>(Promise.resolve());
  const offeneWege = useRef(0);
  const [ziehen, setZiehen] = useState<{ gid: string; xMm: number; yMm: number } | null>(null);
  const zugRef = useRef<{ g: LayoutGrafik; zeiger: number; startX: number; startY: number;
                          mmProPx: number; jetzt: { xMm: number; yMm: number } } | null>(null);
  const zugAufraeumen = useRef<(() => void) | null>(null);
  useEffect(() => () => { zugAufraeumen.current?.(); }, []);
  // Nach einer 422 des Speicherns: erst `zumErstenFehler()` der Reiterleiste, dann der
  // Fokus aufs erste Feld des gewählten Reiters (Review-Fokus 3). `fehlerSprung` stösst
  // den Sprung an, `fokusWunsch` löst ihn ein, sobald das Panel gezeichnet ist.
  const reiterleiste = useRef<ReiterleisteElement>(null);
  const [fehlerSprung, setFehlerSprung] = useState(0);
  const [fokusWunsch, setFokusWunsch] = useState<string | null>(null);
  const client = p.client;
  const vorschau = useLayoutVorschau((a) => client.previewLayout(p.layoutId, a));

  const ungespeichert = stand !== null && !istGleich(entwurf, stand.entwurf);
  const geaendert = stand !== null && (stand.geaendert || ungespeichert);
  const feldFehler = useMemo(() => ({ ...vorschauFehler, ...speicherFehler }),
    [vorschauFehler, speicherFehler]);

  // Eine neue Antwort der Vorschau ersetzt deren Feldfehler; eine erfolgreiche räumt sie.
  const vorschauFehlerStand = vorschau.fehler;
  useEffect(() => {
    setVorschauFehler(feldFehlerKarte(vorschauFehlerStand?.felder));
  }, [vorschauFehlerStand]);

  useEffect(() => {
    let abgebrochen = false;
    client.getLayout(p.layoutId).then((s) => {
      if (abgebrochen) return;
      setStand(s);
      standRef.current = s;
      entwurfSetzen(s.entwurf);
      vorschau.sofort({ version: s.version, entwurf: s.entwurf, belegart: "ANGEBOT",
                        beleg: { art: "MUSTER" } });
    }).catch((f: unknown) => {
      if (!abgebrochen) setLadeFehler(meldungAus(f, "Das Layout konnte nicht geladen werden."));
    });
    return () => { abgebrochen = true; };
    // `vorschau` und `client` sind je Anzeige stabil genug; neu geladen wird nur bei
    // einem anderen Layout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.layoutId]);

  const onZustand = p.onZustand;
  useEffect(() => { onZustand({ ungespeichert, listeNeu: false }); }, [ungespeichert, onZustand]);

  // Review-Fokus 3, erster Schritt: der Reiter. `zumErstenFehler()` wählt den ersten Reiter
  // mit Fehlerzähler (und meldet ihn über `me-reiter-gewaehlt` → `setRegister`); gemerkt
  // wird das erste Feld dieses Reiters.
  useEffect(() => {
    if (fehlerSprung === 0) return;
    const kennung = reiterleiste.current?.zumErstenFehler() ?? null;
    const ziel = Object.keys(speicherFehler).find((pfad) => {
      const reiter = reiterFuer(pfad);
      return reiter !== null && panelId(reiter) === kennung;
    });
    setFokusWunsch(ziel === undefined ? null : feldId(ziel));
    // Nur je Sprung — `speicherFehler` ist im selben Takt gesetzt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fehlerSprung]);
  // Zweiter Schritt: das Feld, sobald sein Panel gezeichnet ist (gleicher Takt wie der
  // Reiterwechsel). Ein Befund ohne eigenes Feld (die ganze Spaltenliste) lässt den Fokus
  // am Reiter.
  useEffect(() => {
    if (fokusWunsch === null) return;
    document.getElementById(fokusWunsch)?.focus();
    setFokusWunsch(null);
  }, [fokusWunsch, register]);

  /**
   * Der eine Weg hinaus (Escape, Kreuz; Entscheid 55): Stehen ungespeicherte Eingaben da —
   * auch im Zustand „Fremder Stand“ —, fragt die Verwerfen-Rückfrage gestapelt darüber.
   * „Weiter bearbeiten“ lässt Entwurf und Reiter stehen (Review-Fokus 1; den Fokus gibt
   * die Rückfrage selbst an das Feld zurück — kein Nachbau hier); „Verwerfen“ schliesst zur
   * Layoutliste. Gespeicherte, nicht freigegebene Entwürfe gehen nicht verloren — dafür
   * keine Frage. Kein Ablehnungsweg: `darfAbbrechen` löst nur auf (Review A1, H2).
   */
  async function abbrechen(): Promise<void> {
    const jetzt = standRef.current;
    const offen = jetzt !== null && !istGleich(entwurfRef.current, jetzt.entwurf);
    if (await darfAbbrechen(offen)) p.onSchliessen();
  }

  function auftrag(e: Entwurf = entwurf, art: Belegart = belegart,
                   b: BelegWahl = beleg): LayoutVorschauAnfrage {
    return { version: stand?.version ?? 0, entwurf: e, belegart: art,
             beleg: b.art === "MUSTER" ? { art: "MUSTER" }
               : { art: "ECHT", quelle: b.quelle, id: b.id } };
  }

  function fehlerZeigen(f: unknown, ersatz: string): void {
    if (f instanceof ApiError && f.status === 422 && (f.fields ?? []).length > 0) {
      setSpeicherFehler(feldFehlerKarte(f.fields));
      setMeldung({ text: f.message, art: "schlecht" });
      setFehlerSprung((n) => n + 1);
    } else if (f instanceof ApiError && f.status === 409) {
      // Der 409-Rumpf trägt den Repository-Stand, nicht die Sicht (Katalog, Störung) —
      // deshalb lädt „Neu laden" die Sicht über getLayout.
      setFremd({ von: f.conflict?.changedBy ?? "ein anderer Vorgang",
                 am: f.conflict?.changedAt ?? new Date().toISOString() });
      setMeldung(null);
    } else {
      setMeldung({ text: meldungAus(f, ersatz), art: "schlecht" });
    }
  }

  /**
   * Ein neuer Serverstand. `ersetzen`: `true` ersetzt den lokalen Entwurf (Verwerfen,
   * Übernehmen, Neu laden), `false` lässt ihn stehen (Grafik-Wege, Erneut prüfen). Ein
   * ENTWURF als Wert heisst „ersetzen nur, wenn seit dem Absenden nichts getippt wurde"
   * (Speichern, Freigabe — Fixrunde 1, W3): Sonst bleibt die neuere Eingabe stehen und
   * ist eben ungespeichert.
   */
  function uebernimm(neu: Layout, ersetzen: boolean | Entwurf): void {
    setStand(neu);
    standRef.current = neu;
    konfliktRef.current = false;
    const ja = ersetzen === true
      || (ersetzen !== false && istGleich(entwurfRef.current, ersetzen));
    if (ja) {
      entwurfSetzen(neu.entwurf);
      setSpeicherFehler({});
    }
    setFremd(null);
    p.onZustand({ ungespeichert: !istGleich(entwurfRef.current, neu.entwurf), listeNeu: true });
  }

  async function speichern(): Promise<Layout | null> {
    if (stand === null) return null;
    setArbeitet(true);
    setMeldung(null);
    try {
      const gesendet = entwurfRef.current;
      const neu = await client.saveLayoutDraft(stand.id, stand.version, gesendet);
      uebernimm(neu, gesendet);
      setSpeicherFehler({});
      setMeldung({ text: "Entwurf gespeichert.", art: "ok" });
      return neu;
    } catch (f) {
      fehlerZeigen(f, "Der Entwurf konnte nicht gespeichert werden.");
      return null;
    } finally {
      setArbeitet(false);
    }
  }

  /** Freigeben öffnet erst nach dem Speichern eines ungespeicherten Entwurfs — auch aus
   *  der Störungszeile („Erneut freigeben"): Freigegeben wird nie ein Stand, der nicht der
   *  sichtbare ist (Notiz K8 → K10). */
  async function freigabeOeffnen(): Promise<void> {
    if (fremd !== null) return;
    if (ungespeichert && (await speichern()) === null) return;
    setDialog("freigabe");
  }

  async function freigeben(): Promise<void> {
    // Riegel auch hier: kein fremder und kein ungespeicherter Stand geht hinaus.
    if (stand === null || fremd !== null || !istGleich(entwurfRef.current, stand.entwurf)) return;
    setArbeitet(true);
    try {
      // Freigegeben wird der GESPEICHERTE Entwurf; was danach getippt wurde, bleibt stehen.
      const gespeichert = stand.entwurf;
      const neu = await client.publishLayout(stand.id, stand.version);
      uebernimm(neu, gespeichert);
      setMeldung({ text: `Freigegeben — ab jetzt rendert jeder Beleg von `
        + `${neu.unternehmen.name} mit diesem Layout.`, art: "ok" });
    } catch (f) {
      fehlerZeigen(f, "Die Freigabe ist fehlgeschlagen — nichts wurde geändert.");
    } finally {
      setArbeitet(false);
      setDialog(null);
    }
  }

  async function verwerfen(): Promise<void> {
    if (stand === null) return;
    setArbeitet(true);
    try {
      const neu = await client.discardLayout(stand.id, stand.version);
      uebernimm(neu, true);
      vorschau.sofort(auftrag(neu.entwurf));
      setMeldung({ text: "Entwurf verworfen.", art: "ok" });
    } catch (f) {
      fehlerZeigen(f, "Der Entwurf konnte nicht verworfen werden.");
    } finally {
      setArbeitet(false);
      setDialog(null);
    }
  }

  /**
   * Ein GRAFIK-WEG: Serverweg mit dem aktuellen Versionsriegel, eingereiht in die
   * Laufsperre (`wegKette`). Übernommen werden nur Version und Grafiken (der lokale Entwurf
   * bleibt, `uebernimm(…, false)`), danach rendert das Blatt sofort. Eine 422 mit Feldern
   * landet an der Grafik (`gid`; Pfade ohne Listenpräfix `grafiken[i].`), die Meldung im
   * Kopf — beim Anlegen (`gid` „neu") stattdessen beides im Panel unter „SVG hochladen"
   * (Fixrunde 2, W1). Eine 409 führt wie bei Speichern und Verwerfen in den Zustand
   * „Fremder Stand" (K8); wartende Wege fallen dann weg.
   */
  function grafikWeg(gid: string, weg: (version: number) => Promise<Layout>): Promise<void> {
    if (standRef.current === null || fremd !== null) return Promise.resolve();
    offeneWege.current += 1;
    setArbeitet(true);
    const lauf = async (): Promise<void> => {
      try {
        await einGrafikWeg(gid, weg);
      } finally {
        offeneWege.current -= 1;
        if (offeneWege.current === 0) setArbeitet(false);
      }
    };
    // Auch nach einer (nicht erwarteten) Ablehnung des Vorgängers läuft der nächste Weg —
    // sonst sänke `offeneWege` nie, und der Editor bliebe gesperrt (Gesamtreview F4).
    const naechster = wegKette.current.then(lauf, lauf);
    wegKette.current = naechster;
    return naechster;
  }

  async function einGrafikWeg(gid: string, weg: (version: number) => Promise<Layout>):
      Promise<void> {
    const jetzt = standRef.current;
    if (jetzt === null || konfliktRef.current) return;
    setGrafikFehler(null);
    setMeldung(null);
    try {
      const neu = await weg(jetzt.version);
      uebernimm(neu, false);
      vorschau.sofort({ ...auftrag(entwurfRef.current), version: neu.version });
    } catch (f) {
      if (f instanceof ApiError && f.status === 409) {
        konfliktRef.current = true;
        setFremd({ von: f.conflict?.changedBy ?? "ein anderer Vorgang",
                   am: f.conflict?.changedAt ?? new Date().toISOString() });
        return;
      }
      const felder = f instanceof ApiError && f.status === 422
        ? feldFehlerKarte((f.fields ?? []).map((b) => ({
            field: b.field.replace(/^(layout\.)?grafiken\[\d+\]\./, ""), message: b.message })))
        : {};
      const text = meldungAus(f, "Die Grafik konnte nicht gespeichert werden.");
      if (gid === "neu") {
        setGrafikFehler({ gid, felder, meldung: text });
        return;
      }
      if (Object.keys(felder).length > 0) setGrafikFehler({ gid, felder });
      setMeldung({ text, art: "schlecht" });
    }
  }

  /** Die Felder einer Grafik für einen Änderungsweg — aus dem JÜNGSTEN Stand, damit ein
   *  wartender Weg die Änderung seines Vorgängers nicht zurückdreht (Fixrunde 2, H1). */
  function felderVon(g: LayoutGrafik, teil: GrafikTeil, version: number) {
    const a = standRef.current?.grafiken.find((x) => x.id === g.id) ?? g;
    return { version, name: a.name, pages: a.pages, xMm: a.xMm, yMm: a.yMm,
             widthMm: a.widthMm, opacity: a.opacity, caption: a.caption,
             documentKinds: [...a.documentKinds], ...teil };
  }

  function grafikAendern(g: LayoutGrafik, teil: GrafikTeil): Promise<void> {
    return grafikWeg(g.id, (v) => client.updateLayoutGraphic(p.layoutId, g.id,
      felderVon(g, teil, v)));
  }

  function begrenzt(g: LayoutGrafik, xMm: number, yMm: number): { xMm: number; yMm: number } {
    // R-LAGE: x + Breite ≤ 210, 0 ≤ y ≤ 297 — gezogen wird nie über die Blattkante.
    return { xMm: aufZehntel(Math.min(Math.max(xMm, 0), 210 - g.widthMm)),
             yMm: aufZehntel(Math.min(Math.max(yMm, 0), 297)) };
  }

  /** Ziehen mit Zeigerereignissen (Maus, Stift, Finger). Kein Serveraufruf und kein
   *  Rendern unterwegs; der Massstab kommt aus der gezeichneten Seitenbreite. Nur der
   *  Zeiger, der gegriffen hat, zählt; `pointercancel` verwirft den Zug. */
  function zugBeginnen(g: LayoutGrafik, e: ReactPointerEvent<HTMLElement>): void {
    if (arbeitet || fremd !== null || e.button !== 0 || zugRef.current !== null) return;
    const mmProPx = mmJePixel(e.currentTarget.parentElement?.getBoundingClientRect().width ?? 0);
    if (mmProPx === 0) return;
    e.preventDefault();
    setRegister("marke");
    const zeiger = e.pointerId;
    zugRef.current = { g, zeiger, startX: e.clientX, startY: e.clientY, mmProPx,
                       jetzt: { xMm: g.xMm, yMm: g.yMm } };
    setZiehen({ gid: g.id, xMm: g.xMm, yMm: g.yMm });
    const bewegen = (ev: PointerEvent): void => {
      const z = zugRef.current;
      if (z === null || ev.pointerId !== zeiger) return;
      z.jetzt = begrenzt(z.g, z.g.xMm + (ev.clientX - z.startX) * z.mmProPx,
                         z.g.yMm + (ev.clientY - z.startY) * z.mmProPx);
      setZiehen({ gid: z.g.id, ...z.jetzt });
    };
    const aufraeumen = (): void => {
      window.removeEventListener("pointermove", bewegen);
      window.removeEventListener("pointerup", loslassen);
      window.removeEventListener("pointercancel", abbrechen);
      zugAufraeumen.current = null;
      zugRef.current = null;
    };
    function loslassen(ev: PointerEvent): void {
      if (ev.pointerId !== zeiger) return;
      const z = zugRef.current;
      aufraeumen();
      if (z === null || (z.jetzt.xMm === z.g.xMm && z.jetzt.yMm === z.g.yMm)) {
        setZiehen(null);
        return;
      }
      void grafikAendern(z.g, z.jetzt).finally(() => setZiehen(null));
    }
    function abbrechen(ev: PointerEvent): void {
      if (ev.pointerId !== zeiger) return;
      aufraeumen();
      setZiehen(null);
    }
    window.addEventListener("pointermove", bewegen);
    window.addEventListener("pointerup", loslassen);
    window.addEventListener("pointercancel", abbrechen);
    zugAufraeumen.current = aufraeumen;
  }

  function tasteAufRahmen(g: LayoutGrafik, e: ReactKeyboardEvent<HTMLElement>): void {
    const schritt = e.shiftKey ? 10 : 1;
    const richtungen: Record<string, [number, number]> = {
      ArrowLeft: [-schritt, 0], ArrowRight: [schritt, 0],
      ArrowUp: [0, -schritt], ArrowDown: [0, schritt] };
    const d = richtungen[e.key];
    if (d === undefined) return;
    // Auch während ein Weg läuft: die Pfeiltaste scrollt nicht das Blatt weg.
    e.preventDefault();
    if (arbeitet || fremd !== null || zugRef.current !== null) return;
    const lage = begrenzt(g, g.xMm + d[0], g.yMm + d[1]);
    if (lage.xMm !== g.xMm || lage.yMm !== g.yMm) void grafikAendern(g, lage);
  }

  /** Die Bildquelle einer festen Grafik — SVG oder Rasterbild nach `bildTyp`
   *  (Entscheid 25). */
  function grafikBildUrl(gid: string): string {
    if (stand === null) return "";
    const typ = stand.grafiken.find((g) => g.id === gid)?.bildTyp ?? null;
    return typ === "image/png" || typ === "image/jpeg"
      ? client.layoutGraphicImageUrl(stand.id, gid, stand.version)
      : client.layoutGraphicUrl(stand.id, gid, stand.version);
  }

  function rahmen(seite: number): ReactNode {
    if (stand === null) return null;
    return stand.grafiken.filter((g) => grafikAufSeite(g, seite, belegart)).map((g) => {
      const lage = ziehen?.gid === g.id ? ziehen : g;
      return (
        <div key={g.id} role="button" tabIndex={0} aria-label={`${g.name} verschieben`}
          aria-disabled={arbeitet || fremd !== null}
          className={`lay__rahmen${ziehen?.gid === g.id ? " lay__rahmen--zieht" : ""}`}
          style={{ left: `${(lage.xMm / 210) * 100}%`, top: `${(lage.yMm / 297) * 100}%`,
                   width: `${(g.widthMm / 210) * 100}%` }}
          onPointerDown={(e) => zugBeginnen(g, e)}
          onKeyDown={(e) => tasteAufRahmen(g, e)}>
          <span className="lay__rahmenname">{g.name}</span>
          {/* Während des Ziehens zeigt das Seitenbild die Grafik noch am alten Ort — der
              Rahmen trägt sie darum als Schatten mit (SVG wie PNG/JPEG, Entscheid 25). */}
          {ziehen?.gid === g.id && g.kind === "STATIC" && (
            <img className="lay__rahmenbild" src={grafikBildUrl(g.id)} alt="" draggable={false} />
          )}
        </div>
      );
    });
  }

  async function ablageUebernehmen(): Promise<void> {
    if (stand === null) return;
    setArbeitet(true);
    try {
      const neu = await client.adoptLayoutStore(stand.id, stand.version);
      uebernimm(neu, true);
      vorschau.sofort(auftrag(neu.entwurf));
      setMeldung({ text: "Der Stand der Ablage ist übernommen.", art: "ok" });
    } catch (f) {
      fehlerZeigen(f, "Der Stand der Ablage konnte nicht übernommen werden.");
    } finally {
      setArbeitet(false);
    }
  }

  /** Die Sicht neu holen. `mitEntwurf`: auch den lokalen Entwurf ersetzen (nach 409,
   *  bei ungespeicherten Eingaben erst nach Rückfrage); sonst bleibt er (Erneut prüfen).
   *
   *  Erneut prüfen (K8 N1): Hat inzwischen jemand anders gespeichert (neue Version) und
   *  stehen hier ungespeicherte Eingaben, wird NICHTS übernommen — der Editor geht in den
   *  Zustand „Fremder Stand"; sonst überschriebe das nächste Speichern die fremde Änderung
   *  ohne 409. Ohne eigene Eingaben ersetzt der neue Stand den Entwurf (nichts geht
   *  verloren, und der alte Entwurf gälte sonst als ungespeichert). */
  async function neuLaden(mitEntwurf: boolean): Promise<void> {
    const alt = stand;
    setArbeitet(true);
    try {
      const neu = await client.getLayout(p.layoutId);
      const eigeneEingaben = alt !== null && !istGleich(entwurfRef.current, alt.entwurf);
      if (!mitEntwurf && alt !== null && neu.version !== alt.version && eigeneEingaben) {
        setFremd({ von: "ein anderer Vorgang", am: new Date().toISOString() });
        setMeldung(null);
        return;
      }
      const ersetzen = mitEntwurf || !eigeneEingaben;
      uebernimm(neu, ersetzen);
      setMeldung(null);
      vorschau.sofort({ ...auftrag(ersetzen ? neu.entwurf : entwurfRef.current),
                        version: neu.version });
    } catch (f) {
      setMeldung({ text: meldungAus(f, "Das Layout konnte nicht geladen werden."),
                   art: "schlecht" });
    } finally {
      setArbeitet(false);
      setDialog(null);
    }
  }

  async function echteLaden(): Promise<void> {
    setDialog("echt");
    setEchte(null);
    try {
      setEchte((await client.listLayoutRealDocuments(p.layoutId)).items);
    } catch (f) {
      setEchte([]);
      setMeldung({ text: meldungAus(f, "Die Belege konnten nicht geladen werden."),
                   art: "schlecht" });
    }
  }

  function belegWaehlen(b: BelegWahl): void {
    setBeleg(b);
    setDialog(null);
    vorschau.sofort(auftrag(entwurf, belegart, b));
  }


  if (stand === null) {
    // Derselbe Arbeitsdialog wie danach (gleicher Baum): Er öffnet sofort und bleibt offen,
    // wenn der Stand eintrifft. Ohne Stand gibt es nichts zu verwerfen.
    return (
      <Arbeitsdialog titel="Belegdesign" onAbbrechen={() => p.onSchliessen()}
        meldungen={ladeFehler === null ? undefined : (
          <p className="lay__meldung lay__meldung--schlecht" role="alert">{ladeFehler}</p>
        )}>
        {ladeFehler === null && <p className="lay__hinweis" role="status">Lädt …</p>}
      </Arbeitsdialog>
    );
  }

  const k = stand.katalog;
  const wege: GrafikWege = {
    grafikUrl: (gid) => grafikBildUrl(gid),
    arbeitet: arbeitet || fremd !== null, fehler: grafikFehler, ziehen,
    onHochladen: (datei) => {
      void grafikWeg("neu", (v) => client.createLayoutGraphic(stand.id,
        { version: v, kind: "STATIC", name: grafikNameAus(datei.name), ...STEMPEL_VORGABE },
        datei));
    },
    onUnterschriftsplatz: () => {
      void grafikWeg("neu", (v) => client.createLayoutGraphic(stand.id,
        { version: v, kind: "SIGNATURE", ...UNTERSCHRIFT_VORGABE }, null));
    },
    onAendern: (g, teil) => { void grafikAendern(g, teil); },
    onEntfernen: (g) => {
      void grafikWeg(g.id, (v) => client.deleteLayoutGraphic(stand.id, g.id, v));
    },
    onVerschieben: (von, nach) => {
      // Die Reihenfolge erst IM Weg aus dem jüngsten Stand (Gesamtreview F5, wie felderVon).
      void grafikWeg("reihenfolge", (v) => client.reorderLayoutGraphics(stand.id, v,
        verschoben((standRef.current ?? stand).grafiken.map((g) => g.id), von, nach)));
    },
    onUnternehmen: () => p.onUnternehmen(stand.companyId),
  };
  // Das Startregister bleibt „Seite" (K8); beim Ziehen springt der Editor auf „Marke",
  // damit die Lage gleichzeitig im Register steht (Spec).
  const eintraege: RegisterEintrag[] = [
    { id: "marke", label: "Marke", panel: (pp) => <MarkePanel {...pp} wege={wege} /> },
    { id: "seite", label: "Seite", panel: (pp) => <SeitePanel {...pp} /> },
    { id: "sektionen", label: "Sektionen", panel: (pp) => <SektionenPanel {...pp} /> },
    { id: "tabelle", label: "Tabelle", panel: (pp) => <TabellePanel {...pp} /> },
    { id: "bloecke", label: "Blöcke", panel: (pp) => <BloeckePanel {...pp} /> },
    { id: "texte", label: "Texte", panel: (pp) => <TextePanel {...pp} /> },
  ];
  // Fehlerzähler je Reiter: Feldbefunde über die Zuordnung Feldpfad → Reiter, dazu die
  // Befunde einer Grafik am Reiter „Marke“.
  const fehlerZahl = fehlerJeReiter(feldFehler);
  if (grafikFehler !== null) {
    const n = Object.keys(grafikFehler.felder).length
      || (grafikFehler.meldung === undefined ? 0 : 1);
    if (n > 0) fehlerZahl.marke = (fehlerZahl.marke ?? 0) + n;
  }
  const raender = {
    oben: mm(wertAn(entwurf, ["seitenraender", "oben"]), k.seitenraender.oben),
    rechts: mm(wertAn(entwurf, ["seitenraender", "rechts"]), k.seitenraender.rechts),
    unten: mm(wertAn(entwurf, ["seitenraender", "unten"]), k.seitenraender.unten),
    links: mm(wertAn(entwurf, ["seitenraender", "links"]), k.seitenraender.links),
  };
  const chip = stand.stoerung === "ABLAGE_NICHT_ERREICHBAR"
    ? { text: "Ablage nicht erreichbar", art: "warnung" }
    : stand.stoerung !== null ? { text: "Störung", art: "fehler" }
    : geaendert ? { text: "Entwurf geändert", art: "warnung" }
    : stand.freigegeben !== null ? { text: "freigegeben, keine Änderungen", art: "ok" }
    : { text: "nicht freigegeben", art: "aus" };
  const blattFehler = vorschau.fehler === null ? null
    : vorschau.fehler.felder.length > 0
      ? `${vorschau.fehler.text} (${vorschau.fehler.felder.map((f) => f.field).join(", ")})`
      : vorschau.fehler.text;
  const titel = `Layout „${stand.name}“ — ${stand.unternehmen.name}`;
  // Nach einer 409 (Fremder Stand) sind Felder UND Fuss gesperrt, bis „Neu laden“ den
  // aktuellen Stand geholt hat (Review-Fokus 2).
  const gesperrt = fremd !== null;
  const speichernAus = !ungespeichert || arbeitet || gesperrt;
  const verwerfenAus = !geaendert || arbeitet || gesperrt;
  const freigebenAus = verwerfenAus;

  /* Meldungen VOR allem anderen und AUSSERHALB der Kennwerte (Hausregel; Fixrunde 1, W4):
     Ein eingeklappter Steckbrief darf keinen Fehler verschlucken. Schlechtestes oben. */
  const meldungen: ReactNode[] = [];
  if (fremd !== null) {
    meldungen.push(
      <div key="fremd" className="lay__stoerung" role="alert" aria-label="Fremder Stand">
        <p>Das Layout wurde zwischenzeitlich geändert ({fremd.von},{" "}
          {DATUM.format(new Date(fremd.am))}). Speichern, Verwerfen und Freigeben sind
          gesperrt, bis der aktuelle Stand geladen ist.</p>
        <button type="button" className="lay__knopf" disabled={arbeitet}
          onClick={() => {
            if (ungespeichert) setDialog("neuladen");
            else void neuLaden(true);
          }}>Neu laden</button>
      </div>,
    );
  }
  if (stand.stoerung !== null) {
    meldungen.push(
      // Unerreichbare Ablage ist eine WARNUNG (Chip „warnung", K8 N2): Statuszeile in
      // Warnfarbe. Abweichende oder fehlende Ablage bleiben Alarm in Fehlerfarbe.
      <div key="stoerung" aria-label="Störung"
        {...(stand.stoerung === "ABLAGE_NICHT_ERREICHBAR"
          ? { className: "lay__stoerung lay__stoerung--warnung", role: "status" }
          : { className: "lay__stoerung", role: "alert" })}>
        <p>{stand.stoerung === "ABLAGE_WEICHT_AB"
          ? "Ablage weicht ab: In der Render-Engine liegt ein anderer Stand als der "
            + "hier freigegebene — jemand hat die Ablage direkt beschrieben."
          : stand.stoerung === "ABLAGE_NICHT_ERREICHBAR"
            // Der Server unterscheidet „antwortet mit Fehler“ von „nicht erreichbar“
            // (Nachlese LE1); ohne Hinweis der feste Wortlaut.
            ? stand.ablageHinweis ?? "Engine-Ablage nicht erreichbar — Abgleich offen."
            : "Ablage fehlt: Die Render-Engine kennt dieses Layout nicht mehr — Belege "
              + "rendern mit dem Systemlayout."}</p>
        {stand.stoerung === "ABLAGE_WEICHT_AB" && (
          <button type="button" className="lay__knopf" disabled={arbeitet || gesperrt}
            onClick={() => { void ablageUebernehmen(); }}>Ablage übernehmen</button>
        )}
        {stand.stoerung === "ABLAGE_NICHT_ERREICHBAR" ? (
          <button type="button" className="lay__knopf" disabled={arbeitet || gesperrt}
            onClick={() => { void neuLaden(false); }}>Erneut prüfen</button>
        ) : (
          <button type="button" className="lay__knopf" disabled={arbeitet || gesperrt}
            onClick={() => { void freigabeOeffnen(); }}>Erneut freigeben</button>
        )}
      </div>,
    );
  }
  if (meldung !== null) {
    meldungen.push(
      <p key="meldung" className={`lay__meldung lay__meldung--${meldung.art}`}
        role={meldung.art === "schlecht" ? "alert" : "status"}>{meldung.text}</p>,
    );
  }
  // Bei ABLAGE_NICHT_ERREICHBAR steht der Hinweis schon in der Störung.
  if (stand.ablageHinweis !== null && stand.stoerung !== "ABLAGE_NICHT_ERREICHBAR") {
    meldungen.push(
      <p key="hinweis" className="lay__hinweis" role="status">{stand.ablageHinweis}</p>,
    );
  }

  return (
    <Arbeitsdialog titel="Belegdesign" onAbbrechen={abbrechen} abbrechenGesperrt={arbeitet}
      steckbrief={(
        <Steckbrief art="dialog" titel={titel} kennung={stand.layoutRef}
          status={<span className={`lay__chip lay__chip--${chip.art}`}>{chip.text}</span>}
          werte={(
            <dl className="lay__kennwerte">
              <dt>Unternehmen</dt>
              <dd>{stand.unternehmen.name} ({stand.unternehmen.kuerzel}){" "}
                <Knopf art="leise" onClick={() => p.onUnternehmen(stand.companyId)}>
                  Unternehmen öffnen</Knopf></dd>
              <dt>Freigabe</dt>
              <dd>{stand.freigegeben === null ? "noch nicht freigegeben"
                : `freigegeben am ${DATUM.format(new Date(stand.freigegeben.am))}`
                  + (stand.freigegeben.von === null ? "" : ` von ${stand.freigegeben.von}`)}</dd>
            </dl>
          )} />
      )}
      meldungen={meldungen.length > 0 ? meldungen : undefined}
      fussLinks={(
        <Knopf art="sekundaer" deaktiviert={verwerfenAus}
          onClick={() => { if (!verwerfenAus) setDialog("verwerfen"); }}>Verwerfen</Knopf>
      )}
      hinweis={ungespeichert ? "Ungespeicherte Änderungen"
        : geaendert ? "Entwurf gespeichert, nicht freigegeben" : "Keine Änderungen"}
      fuss={(
        <>
          <Knopf art="primaer" deaktiviert={speichernAus}
            onClick={() => { if (!speichernAus) void speichern(); }}>Speichern</Knopf>
          <Knopf art="sekundaer" deaktiviert={freigebenAus}
            onClick={() => { if (!freigebenAus) void freigabeOeffnen(); }}>Freigeben</Knopf>
        </>
      )}>
      <div className="lay">
        <div className="lay__arbeit" data-designer="rechts">
          <div className="lay__blattspalte">
            <div className="lay__werkzeuge">
              <label className="lay__werkzeug">
                <span className="lay__sr">Belegart</span>
                <select aria-label="Belegart" value={belegart}
                  onChange={(e) => {
                    const art = e.target.value as Belegart;
                    setBelegart(art);
                    vorschau.sofort(auftrag(entwurf, art));
                  }}>
                  {k.belegarten.map((b) => <option key={b.art} value={b.art}>{b.label}</option>)}
                </select>
              </label>
              <button type="button" className="lay__knopf" aria-pressed={beleg.art === "MUSTER"}
                onClick={() => belegWaehlen({ art: "MUSTER" })}>Muster</button>
              <button type="button" className="lay__knopf" aria-pressed={beleg.art === "ECHT"}
                onClick={() => { void echteLaden(); }}>
                {beleg.art === "ECHT" ? beleg.titel : "Echter Beleg …"}
              </button>
              <button type="button" className="lay__knopf" aria-pressed={zoom === "breite"}
                onClick={() => setZoom("breite")}>Breite</button>
              <button type="button" className="lay__knopf" aria-pressed={zoom === "voll"}
                onClick={() => setZoom("voll")}>100 %</button>
              <label className="lay__werkzeug">
                <input type="checkbox" checked={hilfslinien}
                  onChange={(e) => setHilfslinien(e.target.checked)} /> Hilfslinien
              </label>
              <label className="lay__werkzeug">
                <input type="checkbox" checked={spaltenlinien}
                  onChange={(e) => setSpaltenlinien(e.target.checked)} /> Spaltenlinien
              </label>
              <button type="button" className="lay__knopf" disabled={vorschau.laedt}
                onClick={() => vorschau.sofort(auftrag())}>Aktualisieren</button>
              <span className="lay__zaehler">
                {vorschau.stand === null ? "" : vorschau.stand.anzahl === 1 ? "1 Seite"
                  : `${vorschau.stand.anzahl} Seiten`}
              </span>
            </div>
            {(vorschau.stand?.hinweise ?? []).length > 0 && (
              <ul className="lay__vorschauhinweise">
                {vorschau.stand!.hinweise.map((h) => <li key={h}>{h}</li>)}
              </ul>
            )}
            <LayoutBlatt vorschau={vorschau.stand} laedt={vorschau.laedt} fehler={blattFehler}
              zoom={zoom} hilfslinien={hilfslinien} raender={raender}
              spaltenlinien={spaltenlinien
                ? spaltenLinien(spaltenAus(entwurf, k.spalten)) : null}
              ueberlagerung={rahmen} />
          </div>
          <div className="lay__gestaltung">
            {/* Die Reiter stehen DIREKT über ihrem Panel (Hausregel „Reiter nach unten
                offen“: aktiver Reiter und Panel sind EINE Fläche) — nicht im Slot `register`
                des Arbeitsdialogs, der über Blatt UND Gestaltung reichte (Review A2 W1; bis
                me-design Befund 30 beantwortet). Im Einspalter unter 900 px stehen sie so
                über dem Panel unter dem Blatt. */}
            <LayoutReiterleiste ref={reiterleiste} eintraege={eintraege} aktiv={register}
              onAktiv={setRegister} fehler={fehlerZahl} />
            {/* `fieldset disabled` sperrt im Zustand „Fremder Stand“ jedes Feld des Panels
                auf einmal (Review-Fokus 2) — ohne den Baum umzubauen. */}
            <fieldset className="lay__sperre" disabled={gesperrt}>
              <LayoutRegisterPanel eintraege={eintraege} aktiv={register}
                stand={stand} entwurf={entwurf} feldFehler={feldFehler} belegart={belegart}
                onEntwurf={eingabe}
                onFeldVerlassen={() => vorschau.anfordern(auftrag(entwurfRef.current))} />
            </fieldset>
          </div>
        </div>

        {dialog === "neuladen" && (
          <Modal titel="Layout neu laden" onAbbrechen={() => setDialog(null)}
            abbrechenGesperrt={arbeitet}>
            <p>Der aktuelle Stand wird geladen. Ungespeicherte Eingaben gehen dabei verloren.</p>
            <ModalFuss speichernText="Neu laden" speichernGesperrt={arbeitet}
              abbrechenGesperrt={arbeitet} onSpeichern={() => { void neuLaden(true); }}
              onAbbrechen={() => setDialog(null)} />
          </Modal>
        )}

        {dialog === "freigabe" && (
          <Modal titel="Layout freigeben" onAbbrechen={() => setDialog(null)}
            abbrechenGesperrt={arbeitet}>
            {/* Meldung vor der Liste; der Riegel steht auch in freigeben(). */}
            {ungespeichert && (
              <Hinweis art="fehler">Zuerst speichern — ungespeicherte Eingaben würden nicht
                freigegeben.</Hinweis>
            )}
            <p>Ab der Freigabe rendert jeder Beleg von {stand.unternehmen.name} mit diesem
              Layout — Vorschau, PDF, Ablage und E-Rechnung.</p>
            {stand.aenderungen.length > 0 ? (
              <>
                <p>Geändert gegenüber der letzten Freigabe:</p>
                <ul className="lay__aenderungen">
                  {stand.aenderungen.map((a) => <li key={a}>{a}</li>)}
                </ul>
              </>
            ) : <p>Gegenüber der letzten Freigabe ist nichts geändert.</p>}
            <ModalFuss speichernText="Freigeben"
              speichernGesperrt={arbeitet || ungespeichert || gesperrt}
              abbrechenGesperrt={arbeitet} onSpeichern={() => { void freigeben(); }}
              onAbbrechen={() => setDialog(null)} />
          </Modal>
        )}

        {dialog === "verwerfen" && (
          <Modal titel="Entwurf verwerfen" onAbbrechen={() => setDialog(null)}
            abbrechenGesperrt={arbeitet}>
            <p>{stand.freigegeben === null
              ? "Der Entwurf und alle Grafiken werden entfernt — es gilt wieder das Systemlayout."
              : "Entwurf und Grafiken werden auf den freigegebenen Stand zurückgesetzt."}
              {ungespeichert ? " Ungespeicherte Eingaben gehen verloren." : ""}</p>
            <ModalFuss speichernText="Verwerfen" speichernGesperrt={arbeitet}
              abbrechenGesperrt={arbeitet} onSpeichern={() => { void verwerfen(); }}
              onAbbrechen={() => setDialog(null)} />
          </Modal>
        )}

        {dialog === "echt" && (
          <Modal titel="Echten Beleg wählen" onAbbrechen={() => setDialog(null)} breite="mittel">
            {echte === null ? <p className="lay__hinweis">Lädt …</p>
              : echte.length === 0 ? <p className="lay__hinweis">Dieses Unternehmen hat noch keine Belege.</p>
              : (
                <ul className="lay__belegliste">
                  {echte.map((b) => {
                    const text = `${k.belegarten.find((a) => a.art === b.belegart)?.label ?? b.belegart} `
                      + `${b.nummer ?? "Entwurf"} · ${b.datum ?? "—"} · ${b.kunde}`;
                    return (
                      <li key={`${b.quelle}-${b.id}`}>
                        <button type="button" className="lay__belegzeile"
                          onClick={() => {
                            setBelegart(b.belegart);
                            belegWaehlen({ art: "ECHT", quelle: b.quelle, id: b.id, titel: text });
                          }}>{text}</button>
                      </li>
                    );
                  })}
                </ul>
              )}
            <ModalFuss speichernText="Muster verwenden" onSpeichern={() => belegWaehlen({ art: "MUSTER" })}
              onAbbrechen={() => setDialog(null)} />
          </Modal>
        )}
      </div>
    </Arbeitsdialog>
  );
}
